import { useEffect } from 'react';

import { useUser } from '@/features/auth/model/auth.selectors';
import { useAuthStore } from '@/features/auth/model/auth.store';
import { wsLogger } from '@/shared/lib/logger';

import { useConnectionStore } from '../stores/connection.store';
import {
  FOREGROUND_RECONCILE_THROTTLE_MS,
  markForegroundReconcileFired,
  markReconcileFetchFired,
  shouldRunForegroundReconcile,
  shouldRunReconcileFetch,
} from '../reconcile-throttle';
import { getAblyConnectionState } from '../utils';

interface UseTabVisibleReconcileOptions {
  isLeader: boolean;
  /**
   * Called when the tab becomes visible AND Ably is healthy AND both the
   * L3 (foreground, 30s) and L2 (inner, 10s) throttles release. Wired to
   * `handleReconnectSync` from the provider so it shares the same sync
   * bundle (queue + balance + profile + invalidations) as the L2 path.
   */
  onReconnect: () => void | Promise<void>;
}

/**
 * L3 reconcile (per WEB-L3 in docs/REALTIME_RECONCILE_PLAN.md).
 *
 * Covers the case where the tab was hidden BRIEFLY (< 30s grace, so the
 * existing visibility-handler in `useWebSocketConnection` did NOT disconnect
 * Ably) but client-side handlers may have been blocked or dropped events
 * during the hide — mutation lock active, throttled, BroadcastChannel
 * missed delivery, etc. When Ably stayed connected through the hide, the
 * existing visibility-handler does nothing; this hook fills that gap by
 * firing the same sync bundle on `visibilitychange → visible` + healthy
 * connection.
 *
 * If Ably dropped during the hide, the existing handler in
 * `useWebSocketConnection` calls `forceReconnect()` which cascades to the
 * L2 path on `(SUSPENDED|FAILED) → CONNECTED`. This hook short-circuits
 * in that case to avoid double-firing — the L2 inner throttle would
 * absorb it anyway, but skipping early keeps logs clean.
 *
 * Leader-only: followers piggyback on BroadcastChannel updates from the
 * leader; running a parallel reconcile fetch on each follower would just
 * burn quota for the same data.
 */
export function useTabVisibleReconcile({
  isLeader,
  onReconnect,
}: UseTabVisibleReconcileOptions) {
  const user = useUser();
  const token = useAuthStore((state) => state.token);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }
    if (!isLeader || !user?.id || !token) {
      return;
    }

    const handleVisibilityChange = () => {
      if (document.hidden) {
        return;
      }

      const ablyState = readAblyConnectionState();
      // Bad-state branch is owned by `useWebSocketConnection`'s handler:
      // it calls `forceReconnect()` which cascades to L2. Skip here to
      // avoid double-firing the sync bundle.
      if (ablyState !== 'connected') {
        wsLogger.debug(
          `[L3_RECONCILE] reason=foreground skipped (ably=${ablyState ?? 'unknown'})`
        );
        return;
      }

      if (!shouldRunForegroundReconcile()) {
        wsLogger.debug(
          `[L3_RECONCILE] reason=foreground throttled outer (window=${FOREGROUND_RECONCILE_THROTTLE_MS}ms)`
        );
        return;
      }
      markForegroundReconcileFired();

      if (!shouldRunReconcileFetch()) {
        wsLogger.debug(
          '[L3_RECONCILE] reason=foreground throttled inner (L2 fired recently)'
        );
        return;
      }
      markReconcileFetchFired();
      wsLogger.info('[L3_RECONCILE] reason=foreground fired');

      const result = onReconnect();
      if (result instanceof Promise) {
        result.catch((error) => {
          wsLogger.error('[L3_RECONCILE] sync callback failed:', error);
        });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isLeader, user?.id, token, onReconnect]);
}

function readAblyConnectionState(): string | null {
  // Shared reader (`utils.getAblyConnectionState`) — if Echo restructures
  // `connector.ably` this returns null and L3 silently no-ops, which is the
  // correct degradation.
  return getAblyConnectionState(useConnectionStore.getState().echo);
}
